import { displayDateUS } from "../../utils/dateUtils";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

function boldCompany(company) {
  return `<strong>${escapeHtml(company)}</strong>`;
}

function signatureBlock(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

function check(val) {
  return val ? "☑" : "☐";
}

function ol(items) {
  return `<ol class="points">${items
    .map((item) => `<li>${item}</li>`)
    .join("")}</ol>`;
}

/**
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseEmploymentAgreementFromApiResponse(response, targetUserId) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.agreement) return result.agreement;
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null || result.employeeName != null || result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

/**
 * Print-ready HTML for Employment Agreement — point-wise, aligned fields.
 * @param {Record<string, unknown>} agreement
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildEmploymentAgreementPrintHtml(agreement, branding) {
  const company = branding.companyName || "Company";
  const companyHtml = boldCompany(company);
  const logoUrl = escapeHtml(branding.logoUrl || "");

  const employeeName = escapeHtml(String(agreement.employeeName || ""));
  const address = escapeHtml(String(agreement.employeeAddress || ""));
  const phone = escapeHtml(String(agreement.employeePhone || ""));
  const position = escapeHtml(
    String(agreement.position || "Caregiver / Home Care Aide"),
  );
  const hourlyRate = escapeHtml(String(agreement.hourlyRate || ""));
  const effectiveDate = escapeHtml(formatDate(agreement.effectiveDate));
  const ecName = escapeHtml(String(agreement.emergencyContactName || ""));
  const ecPhone = escapeHtml(String(agreement.emergencyContactPhone || ""));
  const ecRel = escapeHtml(String(agreement.emergencyContactRelationship || ""));

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Employment Agreement</title>
<style>
  @page { size: letter; margin: 0.5in; }
  * { box-sizing: border-box; }
  body {
    font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
    font-size: 10pt;
    color: #1e293b;
    line-height: 1.4;
    margin: 0;
  }
  .logo { display: block; max-height: 52px; margin: 0 auto 8px; }
  h1 {
    text-align: center;
    font-size: 15pt;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin: 0 0 14px;
    color: #0f172a;
    font-weight: 700;
  }
  .intro {
    margin: 0 0 12px;
    padding: 10px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #f8fafc;
  }
  .intro p { margin: 0 0 8px; }
  .intro p:last-child { margin: 0; }
  .section {
    margin: 0 0 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    page-break-inside: avoid;
  }
  .section-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    background: #f1f5f9;
    border-bottom: 1px solid #e2e8f0;
    font-size: 10pt;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #0f172a;
  }
  .num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    border-radius: 50%;
    background: #1e293b;
    color: #fff;
    font-size: 8pt;
    font-weight: 700;
  }
  .section-body { padding: 8px 10px 10px; }
  .section-body p { margin: 0 0 6px; }
  .section-body p:last-child { margin-bottom: 0; }
  .grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 14px;
  }
  .grid-3 {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 8px 14px;
  }
  .field { margin: 0; }
  .field .lbl {
    display: block;
    font-size: 7.5pt;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
    margin-bottom: 2px;
  }
  .field .val {
    display: block;
    min-height: 18px;
    padding: 3px 6px;
    border: 1px solid #cbd5e1;
    border-radius: 3px;
    background: #fff;
  }
  .span-2 { grid-column: span 2; }
  .rate-box {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 6px 0 8px;
    padding: 6px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    background: #f8fafc;
  }
  .rate-box .val {
    display: inline-block;
    min-width: 72px;
    padding: 2px 6px;
    border: 1px solid #cbd5e1;
    border-radius: 3px;
    text-align: center;
  }
  ol.points {
    margin: 4px 0 6px;
    padding-left: 1.35em;
  }
  ol.points li { margin: 2px 0; padding-left: 2px; }
  .sub {
    margin: 6px 0;
    padding: 6px 8px;
    border: 1px solid #f1f5f9;
    border-radius: 4px;
    background: #fafbfc;
  }
  .sub .sub-title {
    font-size: 8pt;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #475569;
    margin-bottom: 3px;
  }
  .sub p { margin: 0; }
  .acks { margin: 0; padding: 0; list-style: none; }
  .acks li {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin: 4px 0;
    padding: 5px 8px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    background: #f8fafc;
  }
  .acks .ack-n {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    text-align: center;
    font-size: 8pt;
    font-weight: 700;
    background: #e2e8f0;
    border-radius: 3px;
    line-height: 16px;
  }
  .sig-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-top: 12px;
  }
  .sig-box {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 10px;
    page-break-inside: avoid;
  }
  .sig-box h2 {
    margin: 0 0 8px;
    font-size: 10pt;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 4px;
  }
  .sig-box .field { margin-bottom: 6px; }
  .sig-img { max-height: 44px; max-width: 160px; }
  .sig-empty { color: #94a3b8; }
  strong { font-weight: 700; }
</style>
</head>
<body>
  ${logoUrl ? `<img class="logo" src="${logoUrl}" alt="" />` : ""}
  <h1>Employment Agreement</h1>

  <div class="intro">
    <p>This Employment Agreement ("Agreement") is entered into between ${companyHtml} ("Company") and the Employee named below.</p>
    <div class="grid-2">
      <div class="field"><span class="lbl">Effective Date</span><span class="val">${effectiveDate || "&nbsp;"}</span></div>
    </div>
  </div>

  <div class="section">
    <div class="section-head">Employee Information</div>
    <div class="section-body">
      <div class="grid-2">
        <div class="field"><span class="lbl">Employee Name</span><span class="val">${employeeName || "&nbsp;"}</span></div>
        <div class="field"><span class="lbl">Phone</span><span class="val">${phone || "&nbsp;"}</span></div>
        <div class="field span-2"><span class="lbl">Position</span><span class="val">${position || "&nbsp;"}</span></div>
        <div class="field span-2"><span class="lbl">Address</span><span class="val">${address || "&nbsp;"}</span></div>
      </div>
    </div>
  </div>

  <div class="section">
    <div class="section-head">Emergency Contact</div>
    <div class="section-body">
      <div class="grid-3">
        <div class="field"><span class="lbl">Contact Name</span><span class="val">${ecName || "&nbsp;"}</span></div>
        <div class="field"><span class="lbl">Contact Phone</span><span class="val">${ecPhone || "&nbsp;"}</span></div>
        <div class="field"><span class="lbl">Relationship</span><span class="val">${ecRel || "&nbsp;"}</span></div>
      </div>
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">1</span> Compensation</div>
    <div class="section-body">
      <p>The Employee shall be compensated at the following hourly rates based on the type of assignment accepted and worked:</p>
      <div class="rate-box">
        <strong>Hourly Shifts</strong>
        <span class="val">${hourlyRate || "&nbsp;"}</span>
        <strong>per hour</strong>
      </div>
      ${ol([
        "Compensation shall be paid in accordance with the Company's regular payroll schedule and applicable federal, state, and local wage and hour laws.",
        "The applicable hourly rate shall be determined by the scheduled assignment and approved by the Company prior to the start of each shift.",
        "The Company reserves the right to revise compensation rates based on factors including employee performance, experience, certifications, assignment complexity, geographic location, client-specific requirements, or changes in Company compensation policies. Changes will be communicated in writing before becoming effective.",
        "Certain assignments (holidays, last-minute shifts, specialized care, or high-acuity clients) may be eligible for additional compensation at the sole discretion of the Company. Any differential pay will be communicated and approved prior to the assignment.",
      ])}
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">2</span> Job Responsibilities</div>
    <div class="section-body">
      <p>Employees agree to perform assigned caregiving duties professionally, safely, and compassionately, including but not limited to:</p>
      ${ol([
        "Personal care assistance",
        "Companion care",
        "Meal preparation",
        "Medication reminders (via the Company's Medication Reminder Log sheets provided to the Employee)",
        "Mobility assistance",
        "Light housekeeping",
        "Transportation when authorized",
        "Timekeeping and documentation of services",
        "Reporting changes in client condition immediately",
      ])}
      <p>Employee agrees to follow all Company policies, client care plans, state regulations, and supervisor instructions.</p>
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">3</span> Compliance with Laws</div>
    <div class="section-body">
      <p>Employee agrees to comply with all applicable:</p>
      ${ol([
        "California labor laws",
        "Home Care Services Consumer Protection Act",
        "OSHA safety requirements",
        "HIPAA privacy requirements, where applicable",
        "Abuse reporting laws",
        "Company policies and procedures",
      ])}
      <p>Employee shall maintain all required licenses, certifications, and registrations necessary for employment.</p>
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">4</span> Confidentiality and HIPAA</div>
    <div class="section-body">
      <p>Employee understands that during employment they may have access to confidential information, including:</p>
      ${ol([
        "Client medical information",
        "Personal information",
        "Family information",
        "Financial information",
        "Company business records",
        "Employee records",
        "Pricing information",
        "Care plans",
        "Scheduling information",
      ])}
      <p><strong>Employee agrees to:</strong></p>
      ${ol([
        "Maintain strict confidentiality.",
        "Access confidential information only when necessary to perform assigned duties.",
        "Never disclose confidential information except as authorized by law or Company policy.",
        "Comply with all HIPAA requirements when applicable.",
        "Return all confidential information upon separation from employment.",
      ])}
      <p>This obligation continues after employment ends.</p>
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">5</span> Company Property</div>
    <div class="section-body">
      <p>Employee acknowledges that all Company property remains the exclusive property of ${companyHtml}, including:</p>
      ${ol([
        "Identification badges",
        "Keys",
        "Electronic devices",
        "Documents",
        "Client records",
        "Training materials",
        "Uniforms (if provided)",
        "Scheduling information",
        "Company credit cards",
        "Electronic files",
      ])}
      <p>Upon separation from employment, Employee agrees to immediately return all Company property.</p>
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">6</span> Professional Conduct</div>
    <div class="section-body">
      <p>Employee agrees to:</p>
      ${ol([
        "Treat clients with dignity and respect.",
        "Maintain professional boundaries.",
        "Report abuse, neglect, or exploitation immediately.",
        "Maintain punctuality and reliability.",
        "Follow infection-control procedures.",
        "Maintain accurate documentation.",
        "Refrain from discrimination, harassment, retaliation, or workplace violence.",
        "Maintain appropriate professional appearance.",
      ])}
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">7</span> Non-Solicitation (California-Compliant)</div>
    <div class="section-body">
      ${ol([
        "Nothing in this Agreement is intended to prohibit lawful competition or interfere with any rights protected under California law.",
        "During employment, Employee agrees not to use Company confidential information or trade secrets to divert Company clients or employees for personal benefit.",
      ])}
      <p><strong>Nothing in this Agreement prohibits Employee from:</strong></p>
      ${ol([
        "Accepting future employment elsewhere",
        "Engaging in lawful competition after employment ends",
        "Exercising any rights protected under California law",
      ])}
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">8</span> Electronic Communications</div>
    <div class="section-body">
      <p>The Employee understands that the Company email, electronic devices, software, and communication systems are Company property and may be monitored to the extent permitted by law.</p>
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">9</span> Scheduling &amp; Shift Assignments</div>
    <div class="section-body">
      <div class="sub"><div class="sub-title">Shift Assignments</div><p>The Company will assign shifts based on client needs, caregiver availability, qualifications, and scheduling requirements. Employees are expected to communicate their availability accurately and promptly notify the Company of any changes.</p></div>
      <div class="sub"><div class="sub-title">Shift Length</div><p>Scheduled shifts may range from a minimum of four (4) hours up to twelve (12) hours per individual caregiver within a 24-hour period. Care exceeding twelve (12) hours will generally require coverage by an additional caregiver.</p></div>
      <div class="sub"><div class="sub-title">Acceptance of Assignments</div><p>Once an assignment is accepted and confirmed, the Employee is expected to report on time and complete it unless otherwise directed by the Company.</p></div>
      <div class="sub"><div class="sub-title">Schedule Changes</div><p>Employees must not independently alter start/end times or leave early without prior Company approval, except in an emergency. The Company will make reasonable efforts to provide advance notice of schedule changes.</p></div>
      <div class="sub"><div class="sub-title">Call-Offs and Emergencies</div><p>Notify the Company as soon as possible per call-off procedures. Do not arrange coverage without prior approval from ${companyHtml}.</p></div>
      <div class="sub"><div class="sub-title">Additional Hours</div><p>Additional hours may be offered; acceptance creates an obligation to fulfill the shift unless an emergency or other approved circumstance prevents doing so.</p></div>
      <div class="sub"><div class="sub-title">No Guarantee of Hours</div><p>Employment does not guarantee a minimum number of work hours or client assignments.</p></div>
      <div class="sub"><div class="sub-title">Compliance With Wage and Hour Laws</div><p>All scheduling complies with applicable wage and hour laws. Employees must accurately report all time worked.</p></div>
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">10</span> Safety</div>
    <div class="section-body">
      <p>Employees agree to:</p>
      ${ol([
        "Use proper lifting techniques.",
        "Follow infection-control procedures.",
        "Report workplace injuries immediately.",
        "Report unsafe conditions.",
        "Participate in required safety training.",
      ])}
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">11</span> Time Log Requirements</div>
    <div class="section-body">
      ${ol([
        `Employees must accurately record all hours worked using ${companyHtml}'s designated <strong>Time Log Sheet</strong>. Time Log Sheets will be provided by ${companyHtml}; employees may also print additional copies as needed.`,
        "Record the actual start and end time of each shift, applicable meal or off-duty periods, total hours worked, and the services provided. All entries must be complete and accurate.",
        `<strong>Completed and signed Time Log Sheets must be submitted to ${companyHtml} on the 15th and the last day of each month for payroll processing.</strong>`,
        `Failure to timely submit may delay payroll; employees must still report all hours actually worked, and ${companyHtml} will comply with applicable wage and hour requirements.`,
      ])}
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">12</span> Payroll</div>
    <div class="section-body">
      <p>Compensation is based on verified hours worked and applicable rates of pay. Employee is responsible for submitting accurate time log sheets by established payroll deadlines.</p>
      <p><strong>Payroll Schedule</strong></p>
      ${ol([
        "1st–15th of each month: Pay date is the 20th of the same month.",
        "16th–end of each month: Pay date is the 5th of the following month.",
      ])}
      ${ol([
        "Payment Method: Employees may receive wages through direct deposit or paper check and must provide any required authorization forms.",
        `Changes to payment method require at least five (5) calendar days' notice to ${companyHtml} before the next payroll date. Details are in the Employee Handbook.`,
      ])}
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">13</span> Training</div>
    <div class="section-body">
      ${ol([
        "Employee agrees to complete all required orientation, continuing education, and mandatory in-service training.",
        "Completion of Company-provided training does not create a contractual obligation to remain employed for any minimum period.",
      ])}
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">14</span> Arbitration</div>
    <div class="section-body">
      ${ol([
        "If separately agreed in writing, the Company and Employee may agree to binding arbitration of covered employment-related disputes under California and federal law.",
        "Nothing waives rights that cannot legally be waived or limits an employee's right to file charges or participate in government proceedings.",
      ])}
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">15</span> Resignation and Care Transition</div>
    <div class="section-body">
      <div class="sub"><div class="sub-title">Notice of Resignation</div><p>Employees are requested to provide ${companyHtml} with at least seven (7) calendar days&apos; written notice before voluntarily resigning from employment whenever reasonably possible.</p></div>
      <div class="sub"><div class="sub-title">Caregiver Transition</div><p>When an Employee provides notice of resignation, ${companyHtml} may require the Employee to participate in a reasonable care transition and handoff with a replacement caregiver. This may include providing information regarding the client&apos;s routine, preferences, care needs, household procedures, and other relevant information necessary to ensure a smooth transition of care.</p></div>
      <p>The Employee is expected to cooperate with reasonable transition and handoff activities during their remaining scheduled work time. All time spent performing required transition or handoff duties must be accurately recorded and will be compensated in accordance with applicable law.</p>
      <p>Nothing in this provision limits an Employee&apos;s right to resign or otherwise changes the at-will employment relationship.</p>
    </div>
  </div>

  <div class="section">
    <div class="section-head"><span class="num">16</span> Entire Agreement</div>
    <div class="section-body">
      ${ol([
        "This Agreement represents the entire understanding between the parties regarding these subjects and supersedes prior oral representations.",
        "If any provision is unenforceable, the remaining provisions shall remain in effect to the fullest extent permitted by law.",
        "California law governs this Agreement.",
      ])}
    </div>
  </div>

  <div class="section">
    <div class="section-head">Employee Acknowledgment</div>
    <div class="section-body">
      <ol class="acks">
        <li><span class="ack-n">1</span><span>${check(agreement.ackReadAgreement)} I acknowledge that I have read this Agreement.</span></li>
        <li><span class="ack-n">2</span><span>${check(agreement.ackUnderstandResponsibilities)} I understand my responsibilities.</span></li>
        <li><span class="ack-n">3</span><span>${check(agreement.ackAtWill)} I understand that my employment is at-will.</span></li>
        <li><span class="ack-n">4</span><span>${check(agreement.ackComplyPolicies)} I agree to comply with Company policies and applicable laws.</span></li>
        <li><span class="ack-n">5</span><span>${check(agreement.ackNoGuaranteePeriod)} I understand that this Agreement does not guarantee employment for any specific period.</span></li>
      </ol>
    </div>
  </div>

  <div class="sig-row">
    <div class="sig-box">
      <h2>Employee</h2>
      <div class="field"><span class="lbl">Name</span><span class="val">${employeeName || "&nbsp;"}</span></div>
      <div class="field"><span class="lbl">Signature</span><span class="val">${signatureBlock(agreement.applicantSignature)}</span></div>
      <div class="field"><span class="lbl">Date</span><span class="val">${escapeHtml(formatDate(agreement.applicantSignedAt)) || "&nbsp;"}</span></div>
    </div>
    <div class="sig-box">
      <h2>Employer — ${escapeHtml(company)}</h2>
      <div class="field"><span class="lbl">Authorized Representative</span><span class="val">${escapeHtml(String(agreement.employerAuthorizedRepName || agreement.reviewedBySignedBy || "")) || "&nbsp;"}</span></div>
      <div class="field"><span class="lbl">Title</span><span class="val">${escapeHtml(String(agreement.employerAuthorizedRepTitle || "")) || "&nbsp;"}</span></div>
      <div class="field"><span class="lbl">Signature</span><span class="val">${signatureBlock(agreement.employerSignature || agreement.reviewedBySignature)}</span></div>
      <div class="field"><span class="lbl">Date</span><span class="val">${escapeHtml(formatDate(agreement.employerSignedAt || agreement.reviewedBySignedAt)) || "&nbsp;"}</span></div>
    </div>
  </div>
</body>
</html>`;
}
